import Axios from "axios";
import { BACKEND } from "../constants/config";
import IArea from "../interfaces/IArea";
import IVideo from "../interfaces/IVideo";
import auth from "../resources/api/auth";

const url = `${BACKEND}/appsinti/cameras`
const starredCamerasKey = 'starred_cameras'
const { Authorization } = auth;

const getAreas = async (appId: number): Promise<IArea[]> => {
    try {
        const response = await Axios.get<IArea[]>(url, { headers: { "app-id": appId, Authorization } });
        return response.data as IArea[]
    } catch (error) {
        return []
    }
}

export const getData = async (appId: number): Promise<{ areas: IArea[], previews: IVideo[], starred: IVideo[] }> => {
    try {
        const areas = await getAreas(appId);
        const previews = await getPreviewCameras(appId);
        const starred = getCamerasFromIds(getStarredIds(appId), areas);

        if (previews.length > 0) return { areas, previews, starred };

        const defaultPreviewCameras = getDefaultCameras(areas);
        return { areas, previews: defaultPreviewCameras, starred }
    } catch (error) {
        return { areas: [], previews: [], starred: [] };
    }
}

export const getCamerasFromIds = (ids: string[], areas: IArea[]): IVideo[] => {
    const cameras: IVideo[] = [];

    if (!ids) return cameras;

    for (const area of areas) {
        if (cameras.length === ids.length) {
            break
        }
        for (const camera of area.cameras) {
            if (ids.includes(camera.id)) {
                cameras.push(camera);
            }
        }
    }
    return cameras;
}

const getDefaultCameras = (areas: IArea[]): IVideo[] => {
    const defaultCameras: IVideo[] = []
    const maxDefaultCamerasCount = 2

    for (const area of areas) {
        if (defaultCameras.length === maxDefaultCamerasCount) {
            break;
        }
        defaultCameras.push(area.cameras[0]);
    }

    return defaultCameras;
}

export const getPreviewCameras = async (appId: number): Promise<IVideo[]> => {
    try {
        const response = await Axios.get<IVideo[]>(`${url}/previews`, { headers: { "app-id": appId, Authorization } });
        return response.data as IVideo[];
    } catch (e) {
        return [];
    }
}

export const modifyPreviewCameras = async (previews: string[], appId: number): Promise<boolean> => {
    try {
        await Axios.patch(`${url}/previews`, { previews }, { headers: { "app-id": appId, Authorization } })
        return true
    } catch (e) {
        return false
    }
}

// Starred cameras are device-scoped: each screen keeps its own selection, so
// they live in localStorage and never reach the backend.
const readStarredStore = (): { [key: string]: string[] } => {
    const rawData = localStorage.getItem(starredCamerasKey);

    if (!rawData) return {};

    try {
        return JSON.parse(rawData) as { [key: string]: string[] };
    } catch (e) {
        return {};
    }
}

export const getStarredIds = (appId: number): string[] => {
    return readStarredStore()[appId] ?? [];
}

export const modifyStarredCameras = (ids: string[], appId: number) => {
    const starredCameras = readStarredStore();
    starredCameras[appId] = ids;
    localStorage.setItem(starredCamerasKey, JSON.stringify(starredCameras));
}
